import { autoUpdate, computePosition, offset, shift, flip, hide } from '@floating-ui/dom';
import { onClickOutside } from '@vueuse/core';
import { defineComponent, ref, computed, toRef, watchPostEffect, unref } from 'vue-demi';
import { u as useElevation } from './useElevation-a50ec347.js';
import { u as usePopoverState } from './usePopoverState-b92c698b.js';
import { u as useResponsive } from './useResponsive-a02e95b7.js';
import '@onereach/styles/tailwind.config';
import 'tailwindcss/resolveConfig';
import { s as script$1 } from './OrBottomSheet-2d3d91ef.js';
import { s as script$2 } from './OrTeleport.vue3-8099178c.js';
import { resolveComponent, openBlock, createBlock, withCtx, withDirectives, createElementVNode, createElementBlock, mergeProps, renderSlot, normalizeClass, createCommentVNode, vShow } from 'vue';

var PopoverVariant;
(function (PopoverVariant) {
  PopoverVariant["Auto"] = "auto";
  PopoverVariant["Popover"] = "popover";
  PopoverVariant["BottomSheet"] = "bottom-sheet";
})(PopoverVariant || (PopoverVariant = {}));
var PopoverPlacement;
(function (PopoverPlacement) {
  PopoverPlacement["Top"] = "top";
  PopoverPlacement["TopStart"] = "top-start";
  PopoverPlacement["TopEnd"] = "top-end";
  PopoverPlacement["Bottom"] = "bottom";
  PopoverPlacement["BottomStart"] = "bottom-start";
  PopoverPlacement["BottomEnd"] = "bottom-end";
  PopoverPlacement["Left"] = "left";
  PopoverPlacement["LeftStart"] = "left-start";
  PopoverPlacement["LeftEnd"] = "left-end";
  PopoverPlacement["Right"] = "right";
  PopoverPlacement["RightStart"] = "right-start";
  PopoverPlacement["RightEnd"] = "right-end";
})(PopoverPlacement || (PopoverPlacement = {}));

const Popover = [
// Position
'absolute'];
const PopoverBody = [
// Shape
'rounded-md',
// Spacing
'mx-sm', 'my-sm',
// Theme
'theme-background-base', 'dark:theme-background-base-dark', 'theme-foreground-default', 'dark:theme-foreground-default-dark',
// Elevation
'shadow-contextual', 'dark:shadow-contextual-dark'];
const PopoverBodyPlacements = {
  [PopoverPlacement.Top]: [],
  [PopoverPlacement.TopStart]: [
  // Spacing
  'ml-none'],
  [PopoverPlacement.TopEnd]: [
  // Spacing
  'mr-none'],
  [PopoverPlacement.Bottom]: [],
  [PopoverPlacement.BottomStart]: [
  // Spacing
  'ml-none'],
  [PopoverPlacement.BottomEnd]: [
  // Spacing
  'mr-none'],
  [PopoverPlacement.Left]: [],
  [PopoverPlacement.LeftStart]: [
  // Spacing
  'mt-none'],
  [PopoverPlacement.LeftEnd]: [
  // Spacing
  'mb-none'],
  [PopoverPlacement.Right]: [],
  [PopoverPlacement.RightStart]: [
  // Spacing
  'mt-none'],
  [PopoverPlacement.RightEnd]: [
  // Spacing
  'mb-none']
};

var script = defineComponent({
  components: {
    OrBottomSheet: script$1,
    OrTeleport: script$2
  },
  inheritAttrs: false,
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: PopoverVariant.Auto
    },
    bottomSheetProps: {
      type: Object,
      default: undefined
    },
    trigger: {
      type: HTMLElement,
      default: undefined
    },
    placement: {
      type: String,
      default: PopoverPlacement.Bottom
    },
    offset: {
      type: [Number, Object, Function],
      default: undefined
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    customStyles: {
      type: Array,
      default: () => []
    },
    disableDefaultStyles: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:state', 'open', 'close'],
  expose: ['root', 'state', 'isFlipped', 'isHidden', 'open', 'close', 'toggle'],
  setup(props, context) {
    const {
      isDesktop,
      isMobile
    } = useResponsive();
    // Refs
    const root = ref();
    const bottomSheet = ref();
    const bottomSheetRoot = computed(() => {
      var _a;
      return (_a = bottomSheet.value) === null || _a === void 0 ? void 0 : _a.root;
    });
    // Styles
    const rootStyles = computed(() => ['or-popover-v3', ...Popover, ...props.customStyles]);
    const bodyStyles = computed(() => [...PopoverBody, ...PopoverBodyPlacements[props.placement]]);
    // State
    const {
      state,
      open,
      close,
      toggle
    } = usePopoverState(toRef(props, 'isOpen'), context.emit);
    const isFlipped = ref(false);
    const isHidden = ref(false);
    // Effects
    watchPostEffect(() => {
      const rootElement = unref(root);
      if (rootElement) {
        const {
          trigger,
          placement
        } = props;
        if (trigger) {
          autoUpdate(trigger, rootElement, async () => {
            var _a, _b;
            const {
              x,
              y,
              middlewareData
            } = await computePosition(trigger, rootElement, {
              placement,
              middleware: [offset(props.offset), shift(), flip(), hide()]
            });
            Object.assign(rootElement.style, {
              left: `${x}px`,
              top: `${y}px`,
              visibility: middlewareData.hide.referenceHidden ? 'hidden' : 'visible',
              width: props.fullWidth ? `${trigger.offsetWidth}px` : ''
            });
            isFlipped.value = Boolean((_a = middlewareData.flip) === null || _a === void 0 ? void 0 : _a.index);
            isHidden.value = Boolean((_b = middlewareData.hide) === null || _b === void 0 ? void 0 : _b.referenceHidden);
          });
        }
      }
    });
    // #region Elevation
    useElevation(root, state);
    // #endregion
    // #region Click Outside
    const trigger = toRef(props, 'trigger');
    onClickOutside(root, event => {
      const path = event.composedPath();
      if (!path.some(_ref => {
        let {
          classList
        } = _ref;
        return (classList === null || classList === void 0 ? void 0 : classList.contains('or-popover-v3')) || (classList === null || classList === void 0 ? void 0 : classList.contains('or-bottom-sheet-v3'));
      })) {
        close();
      }
    }, {
      ignore: [trigger]
    });
    // #endregion
    return {
      isDesktop,
      isMobile,
      root,
      bottomSheet,
      bottomSheetRoot,
      rootStyles,
      bodyStyles,
      state,
      isFlipped,
      isHidden,
      open,
      close,
      toggle
    };
  }
});

const _hoisted_1 = {
  class: /*#__PURE__*/normalizeClass(['contents'])
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrBottomSheet = resolveComponent("OrBottomSheet");
  const _component_OrTeleport = resolveComponent("OrTeleport");
  return openBlock(), createBlock(_component_OrTeleport, null, {
    default: withCtx(() => [withDirectives(createElementVNode("div", _hoisted_1, [_ctx.isDesktop && _ctx.variant === 'auto' || _ctx.variant === 'popover' ? (openBlock(), createElementBlock("div", mergeProps({
      key: 0,
      ref: "root"
    }, _ctx.$attrs, {
      class: _ctx.rootStyles
    }), [_ctx.disableDefaultStyles ? renderSlot(_ctx.$slots, "default", {
      key: 0
    }) : (openBlock(), createElementBlock("div", {
      key: 1,
      class: normalizeClass(_ctx.bodyStyles)
    }, [renderSlot(_ctx.$slots, "default")], 2 /* CLASS */))], 16 /* FULL_PROPS */)) : createCommentVNode("v-if", true), _ctx.isMobile && _ctx.variant === 'auto' || _ctx.variant === 'bottom-sheet' ? (openBlock(), createBlock(_component_OrBottomSheet, mergeProps({
      key: 1,
      ref: "bottomSheet"
    }, _ctx.bottomSheetProps, {
      "is-open": _ctx.state === 'open',
      "onUpdate:state": _cache[0] || (_cache[0] = $event => _ctx.state = $event)
    }), {
      default: withCtx(() => [renderSlot(_ctx.$slots, "default")]),
      _: 3 /* FORWARDED */
    }, 16 /* FULL_PROPS */, ["is-open"])) : createCommentVNode("v-if", true)], 512 /* NEED_PATCH */), [[vShow, _ctx.state === 'open']])]),
    _: 3 /* FORWARDED */
  });
}

script.render = render;
script.__file = "src/components/or-popover-v3/OrPopover.vue";

export { PopoverPlacement as P, PopoverVariant as a, script as s };
